import { GridFilterItem } from '@mui/x-data-grid-pro';

import { HttpMethod, httpClient, httpGet } from './http-client';
import {
  PartExistance,
  NSPartExistance,
  BulkUpdatePartDataset,
  PartHistoryMovementListFilterOptions,
  UpdatePartLocationObject,
  PartObject,
  PartConditionDropdown,
  PartLocationObject,
  PartHistoryDataType,
  PartHistoryMovementListResponse,
  DuplicatePartsDataType,
  PartHistory,
  HistoryType,
} from '../@types/part.type';
import { PartActionHistoryDataType } from '../@types/partHistory.type';
import { WarrantyPanalType } from '../@types/warrantySummary.type';
import { DuplicatePartsPanelProps } from '../components/molecules/DuplicatePartsPanel/DuplicatePartsPanel.props';
import { PartTypeLocationItemData } from '../components/molecules/PartTypeLocationItem';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

// Response type for part update mutations
interface PartUpdateResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for bulk update file processing
interface BulkUpdateFileResponse {
  success: boolean;
  processedCount?: number;
  errors?: Array<{ row: number; message: string }>;
}

// Response type for existing serials check
interface ExistingSerialsResponse {
  serials: Array<{ serial1: string; exists: boolean }>;
}

export const getPartById = (id: number, signal?: AbortSignal): Promise<PartObject> => {
  return httpGet<PartObject>(`/parts/${id}`, signal);
};

export const getPartByIdForService = (id: number, signal?: AbortSignal): Promise<PartObject> => {
  return httpGet<PartObject>(`/parts/${id}/forService`, signal);
};

export const getPartConditions = (signal?: AbortSignal): Promise<PartConditionDropdown[]> => {
  return httpGet<PartConditionDropdown[]>('/partConditions', signal);
};

export const getPartConditionsForService = (
  signal?: AbortSignal
): Promise<PartConditionDropdown[]> => {
  return httpGet<PartConditionDropdown[]>('/partConditionsForService', signal);
};

export const getHistoryTypes = (signal?: AbortSignal): Promise<HistoryType[]> => {
  return httpGet<HistoryType[]>('/historyTypes', signal);
};

export const updatePart = (id: number, part: Partial<PartObject>): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}`, HttpMethod.Put, part);
};

export const updatePartForService = (
  id: number,
  part: Partial<PartObject>
): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}/forService`, HttpMethod.Put, part);
};

export const getAllWarrantySummary = (
  partId: number,
  signal?: AbortSignal
): Promise<WarrantyPanalType[]> => {
  return httpGet<WarrantyPanalType[]>(`/warranties/getByPart/${partId}`, signal);
};

export const getPartLocationDataById = (
  id: number,
  signal?: AbortSignal
): Promise<PartLocationObject> => {
  return httpGet<PartLocationObject>(`/parts/locationData/${id}`, signal);
};

export const getPartLocationDataByPartId = (
  id: number,
  locationData: UpdatePartLocationObject,
  signal?: AbortSignal
): Promise<PartLocationObject> => {
  const { locationId, locationTypeCode, locationHierarchy } = locationData;

  return httpGet<PartLocationObject>(
    `/parts/locationData/${id}/updatePart?locationId=${locationId}&locationTypeCode=${encodeURIComponent(locationTypeCode)}&locationHierarchy=${encodeURIComponent(locationHierarchy)}`,
    signal
  );
};

export const getPartLocationDataByIdForService = (
  id: number,
  signal?: AbortSignal
): Promise<PartLocationObject> => {
  return httpGet<PartLocationObject>(`/parts/locationData/${id}/forService`, signal);
};

export const getPartLocationDataByServiceId = (
  partTypeId: number,
  serviceId: number,
  signal?: AbortSignal
): Promise<PartTypeLocationItemData[]> => {
  return httpGet<PartTypeLocationItemData[]>(
    `/parts/${partTypeId}/byServiceId/${serviceId}`,
    signal
  );
};

export const updatePartLocation = (
  id: number,
  part: Partial<PartLocationObject>
): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>(`/parts/${id}/location`, HttpMethod.Put, part);
};

export const getPartHistoryByPartId = (
  id: number,
  signal?: AbortSignal
): Promise<PartHistoryDataType[]> => {
  return httpGet<PartHistoryDataType[]>(`/parts/${id}/history`, signal);
};

export const getPartExistanceBySerials = (data: PartExistance): Promise<PartExistance[]> => {
  return httpClient<PartExistance[]>('/bulkSearchParts', HttpMethod.Post, data);
};

export const getNSPartExistanceBySerials = (data: NSPartExistance): Promise<NSPartExistance[]> => {
  return httpClient<NSPartExistance[]>('/checkNSPart', HttpMethod.Post, data);
};

export const updateBulkPart = (data: BulkUpdatePartDataset): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>('/bulkUpdateParts', HttpMethod.Put, data);
};

/** Part Movement list filter fields backed by *numeric* (integer) columns on
 *  api-inventory-mgmt's partHistory view. Their `equals` must go out as `=` —
 *  see mapNumericEqualsOperatorForWire. (Asset ID `partId` Equals is also
 *  routed to the dedicated `?partId=` param above; including it here is
 *  harmless and keeps the set complete.) */
export const MOVEMENT_NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'partId',
  'partTypeId',
  'serviceId',
  'shipmentId',
  'quantity',
]);

export const getPartMovementHistory = (
  queryOptions: PartHistoryMovementListFilterOptions,
  signal?: AbortSignal
): Promise<PartHistoryMovementListResponse> => {
  const { filterModel, page, pageSize, sortModel, startDate, endDate, partId, multiSearchQuery } =
    queryOptions;
  let url = `/partMovementHistory?limit=${pageSize}&page=${page + 1}`;

  // The Timestamp range travels on these top-level params as a half-open UTC
  // instant window (ISO-8601 `Z`); the BFF's datetime handler buckets each row
  // by the instant it was created at, not its server-local calendar date.
  // encodeURIComponent guards the `:` separators in the ISO strings. Older
  // saved reports that supplied date-only `YYYY-MM-DD` values flow through the
  // same path unchanged.
  if (startDate && endDate) {
    url += `&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
  }

  if (partId) {
    url += `&partId=${partId}`;
  }

  // Free-text multi-field search, mirroring /services. Trim so a whitespace-only
  // term from a direct API caller is treated as empty (the UI hook already
  // trims). encodeURIComponent guards spaces (e.g. a user name) and any reserved
  // characters in the search term.
  const trimmedMultiSearchQuery = multiSearchQuery?.trim();
  if (trimmedMultiSearchQuery) {
    url += `&multiSearchQuery=${encodeURIComponent(trimmedMultiSearchQuery)}`;
  }

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = mapNumericEqualsOperatorForWire(
      field,
      filter?.operator,
      MOVEMENT_NUMERIC_FILTER_FIELDS
    );
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpGet<PartHistoryMovementListResponse>(url, signal);
};

export const getPartRepairHistory = (
  partId: number,
  signal?: AbortSignal
): Promise<PartHistory[]> => {
  return httpGet<PartHistory[]>(`/partHistory/${partId}/repairHistory`, signal);
};

export const getLatestPartMomentHistory = (
  partId: number,
  signal?: AbortSignal
): Promise<PartHistory> => {
  const url = `/partHistory/latestPartMovement/${partId}`;
  return httpGet<PartHistory>(url, signal);
};

export const getPartHistoryByServiceIdAndHistoryTypeCodes = (
  serviceId: number,
  historyTypes: string[],
  signal?: AbortSignal
): Promise<PartActionHistoryDataType[]> => {
  const historyTypesParam = encodeURIComponent(JSON.stringify(historyTypes));
  return httpGet<PartActionHistoryDataType[]>(
    `/partHistory/services/${serviceId}?historyTypes=${historyTypesParam}`,
    signal
  );
};

export const getDuplicatePartsBySerial1AndPartType = (
  mainPartId: number,
  serial1: string,
  partTypeId: number,
  signal?: AbortSignal
): Promise<DuplicatePartsDataType[]> => {
  return httpGet<DuplicatePartsDataType[]>(
    `/getDuplicateParts/?mainPartId=${mainPartId}&serial1=${encodeURIComponent(serial1)}&partTypeId=${partTypeId}`,
    signal
  );
};

export const mergeDuplicateParts = (
  data: DuplicatePartsPanelProps
): Promise<PartUpdateResponse> => {
  return httpClient<PartUpdateResponse>('/mergeDuplicateParts', HttpMethod.Post, data);
};

export const createExceptionalPart = (part: Partial<PartObject>): Promise<PartObject> => {
  return httpClient<PartObject>('/parts/exception', HttpMethod.Post, part);
};

export const updateExceptionalPart = (
  id: number,
  data: Partial<PartObject>
): Promise<PartObject> => {
  return httpClient<PartObject>(`/parts/${id}/exception`, HttpMethod.Put, data);
};

export const processPartBulkUpdateFile = (fileList: FormData): Promise<BulkUpdateFileResponse> => {
  const headers = { 'Content-Type': 'multipart/form-data' };

  return httpClient<BulkUpdateFileResponse>(
    '/parts/bulkUpdate/fileProcess',
    HttpMethod.Post,
    fileList,
    headers
  );
};

export const getExistingSerials = (data: {
  serials: string[];
}): Promise<ExistingSerialsResponse> => {
  return httpClient<ExistingSerialsResponse>('/parts/getExistingSerials', HttpMethod.Post, data);
};
